import Swal from 'sweetalert2';

/**
 * Standardized Alert Utility for WaapLeads
 * Salesforce Lightning (SLDS) styled dialogs and toasts: white surface,
 * brand-blue primary button, neutral Cancel, normal casing.
 */


const BTN = 'h-8 px-4 rounded text-[13px] font-semibold';
const base = {
  background: '#FFFFFF',
  color: '#181818',
  buttonsStyling: false,
  customClass: {
    popup: 'rounded-lg border border-[#E5E5E5] shadow-xl',
    title: 'text-[18px] font-bold text-[#181818]',
    htmlContainer: 'text-[13px] text-[#444444] leading-relaxed',
    actions: 'gap-2',
    confirmButton: `${BTN} bg-[#0176D3] text-white hover:bg-[#014486]`,
    cancelButton: `${BTN} bg-white border border-[#D8DDE6] text-[#0176D3] hover:bg-[#F3F2F2]`,
    input: 'h-9 rounded-lg border border-[#D8DDE6] text-[13px]',
  },
};
const destructive = { ...base.customClass, confirmButton: `${BTN} bg-[#BA0517] text-white hover:bg-[#8E030F]` };

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  background: '#FFFFFF',
  color: '#181818',
  customClass: { popup: 'rounded-lg border border-[#E5E5E5] shadow-lg', title: 'text-[13px] font-semibold' },
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer);
    toast.addEventListener('mouseleave', Swal.resumeTimer);
  }
});

const alertService = {
  /**
   * Success Message
   * @param {string} title
   * @param {string} text
   */
  success: (title, text = '') => Swal.fire({ ...base, icon: 'success', title, text, confirmButtonText: 'OK' }),

  /**
   * Error Message
   * @param {string} title
   * @param {string} text
   */
  error: (title, text = '') => Swal.fire({ ...base, icon: 'error', title, text, confirmButtonText: 'OK' }),

  /**
   * Confirm Dialog — resolves true when confirmed.
   * @param {string} title
   * @param {string} text
   * @param {string} icon - warning, error, info, success, question, or 'danger'
   *   (a destructive action: red button, error icon)
   * @param {string} confirmText - the specific action, e.g. 'Delete', 'Send'
   */
  confirm: async (title, text = '', icon = 'warning', confirmText = 'Confirm') => {
    const isDestructive = icon === 'danger' || icon === 'error';
    const result = await Swal.fire({
      ...base,
      customClass: isDestructive ? destructive : base.customClass,
      icon: icon === 'danger' ? 'error' : icon,
      title,
      text,
      showCancelButton: true,
      reverseButtons: true, // Cancel on the left, action on the right
      confirmButtonText: confirmText,
      cancelButtonText: 'Cancel',
    });
    return result.isConfirmed;
  },

  /**
   * Single text-input dialog — returns the trimmed string, or null if
   * cancelled/left blank.
   * @param {string} title
   * @param {string} placeholder
   * @param {{confirmText?: string, input?: 'text'|'password'}} opts
   */
  prompt: async (title, placeholder = '', opts = {}) => {
    const { confirmText = 'Save', input = 'text' } = opts;
    const result = await Swal.fire({
      ...base,
      title,
      input,
      inputPlaceholder: placeholder,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: confirmText,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return null;
    const value = (result.value || '').trim();
    return value || null;
  },

  /**
   * Error dialog that also surfaces the raw error code/subcode/trace-id an
   * API returned (e.g. Meta's Graph API error object). Pass whichever of
   * these fields exist; anything missing is omitted.
   * @param {string} title
   * @param {string} message
   * @param {{code?: string|number, subcode?: string|number, type?: string, fbtraceId?: string}} details
   */
  errorDetails: (title, message, details = {}) => {
    const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const rows = [
      details.code != null && ['Code', details.code],
      details.subcode != null && ['Subcode', details.subcode],
      details.type && ['Type', details.type],
      details.fbtraceId && ['Trace ID', details.fbtraceId],
    ].filter(Boolean);

    const detailsHtml = rows.length
      ? `<div style="margin-top:14px;padding:10px 12px;background:#F3F2F2;border:1px solid #D8DDE6;border-radius:4px;text-align:left;font-family:ui-monospace,monospace;font-size:12px;line-height:1.8;">
          ${rows.map(([k, v]) => `<div><span style="color:#706E6B;">${k}:</span> <span style="color:#181818;word-break:break-all;">${esc(v)}</span></div>`).join('')}
        </div>`
      : '';

    return Swal.fire({
      ...base,
      icon: 'error',
      title,
      html: `<div style="text-align:left;font-size:13px;line-height:1.6;">${esc(message || '')}</div>${detailsHtml}`,
      confirmButtonText: 'OK',
    });
  },

  /**
   * Quick Toast Notification
   * @param {string} icon - success, error, warning, info
   * @param {string} title
   */
  toast: (icon, title) => Toast.fire({ icon, title }),
};

export default alertService;
